// Apresentação da tela "Aplicações": rótulos, cores, quais botões cada estado
// libera e formatação de CPU, memória e tempo. Sem React aqui, para testar.

export type AppStatus = 'running' | 'stopped' | 'starting' | 'stopping' | 'restarting' | 'degraded';

// Mesmas cores da página Pipelines (verde/âmbar/vermelho) mais um cinza para "parado".
export const STATUS_INFO: Record<AppStatus, { label: string; color: string }> = {
  running: { label: 'Rodando', color: '#15803d' },
  stopped: { label: 'Parado', color: '#6b7280' },
  starting: { label: 'Iniciando', color: '#a16207' },
  stopping: { label: 'Desligando', color: '#a16207' },
  restarting: { label: 'Reiniciando', color: '#a16207' },
  degraded: { label: 'Com problema', color: '#b91c1c' },
};

export function statusInfo(status: string): { label: string; color: string } {
  return STATUS_INFO[status as AppStatus] ?? { label: status, color: '#6b7280' };
}

// Um só botão de liga/desliga, como no Docker Desktop: quadrado (desligar) quando
// há algo no ar, triângulo (ligar) quando está parado. Em transição (desligando,
// reiniciando) os botões esperam.
export function actionAvailability(status: string, busy: boolean) {
  const idle = !busy;
  return {
    toggle: idle && status !== 'stopping' && status !== 'restarting' ? (status === 'stopped' ? 'start' : 'stop') : undefined,
    restart: idle && (status === 'running' || status === 'degraded'),
  } as { toggle: 'start' | 'stop' | undefined; restart: boolean };
}

const NUMBER = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

export function formatCpu(millicores: number | undefined): string {
  if (millicores === undefined) return '-';
  return millicores >= 1000 ? `${NUMBER.format(millicores / 1000)} CPU` : `${NUMBER.format(millicores)}m`;
}

export function formatMemory(mib: number | undefined): string {
  if (mib === undefined) return '-';
  return mib >= 1024 ? `${NUMBER.format(mib / 1024)} GiB` : `${NUMBER.format(mib)} MiB`;
}

export function timeAgo(iso: string | undefined, now: number = Date.now()): string {
  const then = Date.parse(iso ?? '');
  if (Number.isNaN(then)) return '-';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'agora há pouco';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.round(hours / 24);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

export function summary(apps: { status: string }[]): string {
  const count = (status: string) => apps.filter(app => app.status === status).length;
  const running = count('running');
  const stopped = count('stopped');
  const other = apps.length - running - stopped;
  const parts = [`${running} rodando`, `${stopped} ${stopped === 1 ? 'parada' : 'paradas'}`];
  if (other > 0) parts.push(`${other} em transição ou com problema`);
  return `${apps.length} ${apps.length === 1 ? 'aplicação' : 'aplicações'}: ${parts.join(', ')}`;
}
